/**
 * Direttiva v-permission per controllo permessi nei template
 * 
 * Questa direttiva permette di nascondere/mostrare elementi basandosi sui permessi dell'utente
 * 
 * Utilizzo:
 * v-permission="'users.create'" - Controlla permesso create su controller users
 * v-permission="['users.create', 'users.update']" - Controlla almeno uno dei permessi (OR)
 * v-permission.all="['users.create', 'users.update']" - Controlla tutti i permessi (AND)
 * v-permission.role="'admin'" - Controlla se ha il ruolo admin
 * v-permission.role="['admin', 'manager']" - Controlla se ha almeno uno dei ruoli
 * 
 * Esempi:
 * <v-btn v-permission="'users.create'">Nuovo Utente</v-btn>
 * <v-card v-permission="['users.view', 'users.update']">Contenuto</v-card>
 * <div v-permission.all="['users.create', 'users.delete']">Admin Actions</div>
 * <span v-permission.role="'admin'">Solo Admin</span>
 */

export default {
  name: 'permission',
  
  /**
   * Chiamato quando l'elemento viene inserito nel DOM
   */
  inserted(el, binding, vnode) {
    checkPermission(el, binding, vnode);
  },
  
  /**
   * Chiamato quando il valore del binding cambia
   */
  update(el, binding, vnode) {
    checkPermission(el, binding, vnode);
  }
};

/**
 * Controlla i permessi e nasconde/mostra l'elemento
 * @param {HTMLElement} el - Elemento DOM
 * @param {Object} binding - Oggetto binding della direttiva
 * @param {Object} vnode - Virtual Node
 */
function checkPermission(el, binding, vnode) {
  const { value, modifiers } = binding;
  const { context } = vnode;
  
  if (!value) {
    console.warn('[v-permission]: Nessun valore fornito per la direttiva');
    return;
  }
  
  let hasPermission = false;
  
  try {
    // Controlla se è un controllo sui ruoli
    if (modifiers.role) {
      hasPermission = checkRolePermission(context, value);
    } else {
      // Controllo standard sui permessi
      hasPermission = checkStandardPermission(context, value, modifiers);
    }
    
    // Nasconde/mostra l'elemento basandosi sul permesso
    if (hasPermission) {
      el.style.display = '';
      el.removeAttribute('disabled');
    } else {
      el.style.display = 'none';
      // Per elementi come input o button, li disabilita invece di nasconderli se specificato
      if (modifiers.disable) {
        el.style.display = '';
        el.setAttribute('disabled', 'disabled');
      }
    }
    
  } catch (error) {
    console.error('[v-permission]: Errore nel controllo permessi:', error);
    // In caso di errore, nasconde l'elemento per sicurezza
    el.style.display = 'none';
  }
}

/**
 * Controlla i permessi sui ruoli
 * @param {Object} context - Contesto del componente Vue
 * @param {string|Array} roles - Ruolo/i da controllare
 * @returns {boolean}
 */
function checkRolePermission(context, roles) {
  if (!context || typeof context.hasRole !== 'function') {
    console.warn('[v-permission]: Il componente deve utilizzare PermissionMixin');
    return false;
  }
  
  if (Array.isArray(roles)) {
    return context.hasAnyRole(roles);
  } else {
    return context.hasRole(roles);
  }
}

/**
 * Controlla i permessi standard (controller.action)
 * @param {Object} context - Contesto del componente Vue
 * @param {string|Array} permissions - Permesso/i da controllare
 * @param {Object} modifiers - Modificatori della direttiva
 * @returns {boolean}
 */
function checkStandardPermission(context, permissions, modifiers) {
  if (!context || typeof context.canUser !== 'function') {
    console.warn('[v-permission]: Il componente deve utilizzare PermissionMixin');
    return false;
  }
  
  if (Array.isArray(permissions)) {
    // Se il modificatore 'all' è presente, controlla che abbia tutti i permessi
    if (modifiers.all) {
      return permissions.every(permission => checkSinglePermission(context, permission));
    } else {
      // Altrimenti controlla che abbia almeno uno dei permessi
      return permissions.some(permission => checkSinglePermission(context, permission));
    }
  } else {
    return checkSinglePermission(context, permissions);
  }
}

/**
 * Controlla un singolo permesso
 * @param {Object} context - Contesto del componente Vue
 * @param {string} permission - Permesso in formato 'controller.action'
 * @returns {boolean}
 */
function checkSinglePermission(context, permission) {
  if (typeof permission !== 'string') {
    console.warn('[v-permission]: Il permesso deve essere una stringa in formato "controller.action"');
    return false;
  }
  
  const parts = permission.split('.');
  if (parts.length !== 2) {
    console.warn('[v-permission]: Il permesso deve essere in formato "controller.action"');
    return false;
  }
  
  const [controller, action] = parts;
  return context.canUser(controller, action);
}